import {
    Badge,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
    IconBadge,
    Mono,
    QuizStatCard,
    QuizStatCardGroup,
    Sheet,
    SheetContent,
    SheetTitle,
    Slider,
    Switch,
    Text,
    Textarea,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRightIcon, Pencil, Plus, PlusIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    UpdateVideoContentDocument,
    UpdateTextContentDocument,
    UpdateQuizContentDocument,
    CreateQuizQuestionDocument,
    UpdateQuizQuestionDocument,
    DeleteQuizQuestionDocument,
} from "../../graphql/courses.generated";
import { SetContentTagsDocument, TagOptionsDocument } from "@/graphql/media.generated";
import { BrandsDocument } from "../../graphql/brands.generated";
import { QuizQuestion } from "./QuizQuestion";
import { CourseVideoBuilder } from "./CourseVideoBuilder";
import { CourseVideoPlayer } from "./CourseVideoPlayer";
import { CONTENT_TYPE_META } from "./CourseHelpers";
import type { Module as BaseModule } from "./CoursePreviewPage";

type ContentItem = BaseModule["content"][number] & { moduleName: string };

export interface ContentEditorProps {
    editingContentId: string | null;
    setEditingContentId: React.Dispatch<React.SetStateAction<string | null>>;
    allContent: ContentItem[];
    organizationId: string;
    onToggleVisibility?: (id: string) => void;
    onSave: <T>(fn: () => Promise<T>) => Promise<T>;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
    editingContentId,
    setEditingContentId,
    allContent,
    organizationId,
    onSave,
}) => {
    const { t } = useTranslation();
    const editingIndex = editingContentId !== null ? allContent.findIndex((c) => c.id === editingContentId) : -1;
    const editingContent = editingIndex !== -1 ? allContent[editingIndex] : null;

    const [updateVideo] = useMutation(UpdateVideoContentDocument);
    const [updateText] = useMutation(UpdateTextContentDocument);
    const [updateQuiz] = useMutation(UpdateQuizContentDocument);
    const [createQuestion] = useMutation(CreateQuizQuestionDocument);
    const [updateQuestion] = useMutation(UpdateQuizQuestionDocument);
    const [deleteQuestion] = useMutation(DeleteQuizQuestionDocument);
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const thresholdDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
    const titleRef = useRef<HTMLTextAreaElement>(null);

    const { data: brandsData } = useQuery(BrandsDocument);
    const { data: tagOptionsData } = useQuery(TagOptionsDocument);
    const allTags = tagOptionsData?.tags ?? [];
    const [setContentTags] = useMutation(SetContentTagsDocument);
    // A brand with seesAllContent sees every item regardless of tagging, so offering it
    // here would let someone "select" a brand that does nothing — the mutation drops it
    // server-side anyway (common/database/repositories/contentBrands.ts).
    const allBrands = (brandsData?.brands ?? []).filter((b) => !b.seesAllContent);

    const [
        localTitle,
        setLocalTitle,
    ] = useState<string | undefined>(editingContent?.title ?? "");
    const [
        localBody,
        setLocalBody,
    ] = useState(editingContent?.__typename === "TextContent" ? editingContent.body : "");
    const [
        passThreshold,
        setPassThreshold,
    ] = useState<number[]>([
        editingContent?.__typename === "QuizContent" ? editingContent.passThreshold : 70,
    ]);
    const hasVideo =
        editingContent?.__typename === "VideoContent" &&
        Boolean(editingContent.videoAssetId || editingContent.videoUrl);
    const [
        showVideoUploadZone,
        setShowVideoUploadZone,
    ] = useState<boolean>(!hasVideo);
    const [
        selectedBrands,
        setSelectedBrands,
    ] = useState<Set<string>>(new Set(editingContent?.brands ?? []));
    const [
        brandsOpen,
        setBrandsOpen,
    ] = useState<boolean>(false);
    const [
        selectedTags,
        setSelectedTags,
    ] = useState<Set<string>>(new Set((editingContent?.tags ?? []).map((tag) => tag.id)));
    const [
        tagsOpen,
        setTagsOpen,
    ] = useState<boolean>(false);
    const [
        ageRestricted,
        setAgeRestricted,
    ] = useState<boolean>(editingContent?.ageRestricted ?? false);

    useEffect(() => {
        setLocalTitle(editingContent?.title ?? "");
        setLocalBody(editingContent?.__typename === "TextContent" ? editingContent.body : "");
        if (editingContent?.__typename === "QuizContent") {
            setPassThreshold([editingContent.passThreshold]);
        }
        setShowVideoUploadZone(!hasVideo);
        setSelectedBrands(new Set(editingContent?.brands ?? []));
        setSelectedTags(new Set((editingContent?.tags ?? []).map((tag) => tag.id)));
        setAgeRestricted(editingContent?.ageRestricted ?? false);
    }, [editingContentId]);

    // Grows the title textarea to fit its content; max-h-[3lh] caps the visible box at three
    // lines and scrolls past that, so this only needs to hand it an uncapped height to grow into.
    useEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [
        localTitle,
        editingContentId,
    ]);

    const toggleAgeRestricted = (checked: boolean) => {
        if (!editingContent) return;
        setAgeRestricted(checked);
        const input = { ageRestricted: checked };
        if (editingContent.__typename === "VideoContent") {
            onSave(() => updateVideo({ variables: { id: editingContent.id, input } }));
        } else if (editingContent.__typename === "TextContent") {
            onSave(() => updateText({ variables: { id: editingContent.id, input } }));
        } else if (editingContent.__typename === "QuizContent") {
            onSave(() => updateQuiz({ variables: { id: editingContent.id, input } }));
        }
    };

    const toggleBrand = (slug: string) => {
        if (!editingContent) return;
        const isSelected = selectedBrands.has(slug);
        const next = new Set(selectedBrands);
        isSelected ? next.delete(slug) : next.add(slug);
        setSelectedBrands(next);
        const brands = Array.from(next);
        if (editingContent.__typename === "VideoContent") {
            onSave(() => updateVideo({ variables: { id: editingContent.id, input: { brands } } }));
        } else if (editingContent.__typename === "TextContent") {
            onSave(() => updateText({ variables: { id: editingContent.id, input: { brands } } }));
        } else if (editingContent.__typename === "QuizContent") {
            onSave(() => updateQuiz({ variables: { id: editingContent.id, input: { brands } } }));
        }
    };

    /**
     * Tags go through one mutation for every content type, unlike brands, which are a field on each
     * concrete update input. `content_tags` hangs off the `content` base row — the one every
     * specialization shares — so there is nothing type-specific to branch on here.
     */
    const toggleTag = (tagId: string) => {
        if (!editingContent) return;
        const next = new Set(selectedTags);
        next.has(tagId) ? next.delete(tagId) : next.add(tagId);
        setSelectedTags(next);
        onSave(() => setContentTags({ variables: { contentId: editingContent.id, tagIds: Array.from(next) } }));
    };

    const scheduleContentSave = (newTitle?: string, newBody?: string) => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            if (!editingContent) return;
            if (editingContent.__typename === "VideoContent") {
                onSave(() =>
                    updateVideo({ variables: { id: editingContent.id, input: { title: newTitle || undefined } } }),
                );
            } else if (editingContent.__typename === "TextContent") {
                onSave(() =>
                    updateText({
                        variables: {
                            id: editingContent.id,
                            input: { title: newTitle || undefined, body: newBody ?? undefined },
                        },
                    }),
                );
            } else if (editingContent.__typename === "QuizContent") {
                onSave(() =>
                    updateQuiz({ variables: { id: editingContent.id, input: { title: newTitle || undefined } } }),
                );
            }
        }, 800);
    };

    const handleThresholdChange = (value: number[]) => {
        setPassThreshold(value);
        if (!editingContent || editingContent.__typename !== "QuizContent") return;
        if (thresholdDebounce.current) clearTimeout(thresholdDebounce.current);
        thresholdDebounce.current = setTimeout(() => {
            onSave(() =>
                updateQuiz({
                    variables: { id: editingContent.id, input: { passThreshold: value[0] } },
                }),
            );
        }, 600);
    };

    const serverQuestions = editingContent?.__typename === "QuizContent" ? editingContent.questions : [];
    const [
        localQuestions,
        setLocalQuestions,
    ] = useState(serverQuestions);

    useEffect(() => {
        setLocalQuestions(editingContent?.__typename === "QuizContent" ? editingContent.questions : []);
    }, [editingContentId]);

    const addQuestion = async () => {
        if (!editingContent || editingContent.__typename !== "QuizContent") return;
        const sortOrder = localQuestions.length;
        const res = await onSave(() =>
            createQuestion({
                variables: {
                    input: {
                        quizId: editingContent.id,
                        // A true/false question *is* a single select with these two options — there
                        // is no separate server type any more.
                        questionType: "SINGLE_SELECT",
                        options: [
                            { text: "True", isCorrect: false },
                            { text: "False", isCorrect: false },
                        ],
                        questionText: "",
                        points: 1,
                        sortOrder,
                    },
                },
            }),
        );
        const newQuestion = res.data?.createQuizQuestion;
        if (newQuestion) {
            setLocalQuestions((prev) => [
                ...prev,
                newQuestion,
            ]);
        }
    };

    const handleDeleteQuestion = async (questionId: string) => {
        setLocalQuestions((prev) => prev.filter((q) => q.id !== questionId));
        await onSave(() => deleteQuestion({ variables: { id: questionId } }));
    };

    return (
        <Sheet open={editingContentId !== null} onOpenChange={(open) => !open && setEditingContentId(null)}>
            <SheetContent
                side="right"
                hideClose
                className="bg-background flex w-full max-w-2xl flex-col gap-0 p-0 sm:max-w-2xl"
            >
                {editingContent && (
                    <>
                        <div className="border-border flex h-14 shrink-0 items-center justify-between border-b px-6">
                            <div className="flex min-w-0 items-center gap-2">
                                <SheetTitle className="truncate text-base font-semibold">
                                    Editing {localTitle}
                                </SheetTitle>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        if (editingIndex > 0)
                                            setEditingContentId(allContent[editingIndex - 1]?.id ?? null);
                                    }}
                                    disabled={editingIndex <= 0}
                                    className="border-border hover:bg-muted flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:opacity-30"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="text-text-muted font-mono text-xs whitespace-nowrap">
                                    {(editingIndex ?? 0) + 1} of {allContent.length}
                                </span>
                                <button
                                    onClick={() => {
                                        if (editingIndex < allContent.length - 1)
                                            setEditingContentId(allContent[editingIndex + 1]?.id ?? null);
                                    }}
                                    disabled={editingIndex >= allContent.length - 1}
                                    className="border-border hover:bg-muted flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:opacity-30"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                                <Button
                                    size="sm"
                                    onClick={() => setEditingContentId(null)}
                                    className="ml-2 flex items-center gap-1.5"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    {t("common.actions.done")}
                                </Button>
                            </div>
                        </div>
                        <div className="bg-muted/30 flex-1 overflow-y-auto p-6">
                            <div className="flex flex-col gap-4">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <IconBadge
                                            icon={CONTENT_TYPE_META[editingContent.contentType].icon}
                                            variant={editingContent.contentType}
                                        />
                                        <Mono size="xs" uppercase className="text-text-eyebrow">
                                            {editingContent.contentType} ·{" "}
                                            {editingContent.moduleName || t("admin.lms.untitledModule")}
                                        </Mono>
                                    </div>
                                    <div className="group/title relative">
                                        <textarea
                                            key={editingContent.id}
                                            ref={titleRef}
                                            rows={1}
                                            value={localTitle}
                                            placeholder={t("admin.lms.titlePlaceholder")}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") e.preventDefault();
                                            }}
                                            onChange={(e) => {
                                                setLocalTitle(e.target.value);
                                                scheduleContentSave(e.target.value, localBody);
                                            }}
                                            className="font-display text-text-heading hover:border-border focus:border-secondary focus:bg-card mt-8 max-h-[3lh] min-h-[1lh] w-full resize-none overflow-y-auto rounded-lg border border-transparent bg-transparent p-1.5 pr-8 text-3xl font-bold tracking-tight transition-colors outline-none"
                                        />
                                        <Pencil className="text-muted-foreground/40 pointer-events-none absolute top-1/2 right-2 h-3.5 w-3.5 -translate-y-1/2" />
                                    </div>
                                </div>
                                {editingContent.__typename === "VideoContent" && (
                                    <>
                                        {hasVideo && !showVideoUploadZone && (
                                            <div className="bg-card border-border flex flex-col gap-4 rounded-xl border p-4">
                                                <div
                                                    className="relative shrink-0 overflow-hidden rounded-lg"
                                                    style={{ aspectRatio: "16/9" }}
                                                >
                                                    <CourseVideoPlayer
                                                        contentId={editingContent.id}
                                                        videoUrl={editingContent.videoUrl}
                                                        posterUrl={editingContent.thumbnailUrl}
                                                        assetStatus={editingContent.videoAsset?.status}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-fit"
                                                    onClick={() => {
                                                        setLocalTitle("");
                                                        setShowVideoUploadZone(true);
                                                    }}
                                                >
                                                    {t("common.actions.replaceVideo")}
                                                </Button>
                                            </div>
                                        )}

                                        {showVideoUploadZone && (
                                            <CourseVideoBuilder
                                                contentId={editingContent.id}
                                                organizationId={organizationId}
                                                onTitleChange={setLocalTitle}
                                                onSave={onSave}
                                                toggleShown={
                                                    hasVideo
                                                        ? (shown, hasUploaded) => {
                                                              if (!hasUploaded)
                                                                  setLocalTitle(editingContent.title ?? "");
                                                              setShowVideoUploadZone(shown);
                                                          }
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </>
                                )}
                                {editingContent.__typename === "TextContent" && (
                                    <div className="flex flex-col">
                                        <div className="bg-background overflow-hidden rounded-lg border">
                                            <div className="border-border flex items-center justify-end border-b px-3 py-2">
                                                <Mono size="xs" muted>
                                                    {localBody.length} chars
                                                </Mono>
                                            </div>
                                            <Textarea
                                                key={editingContent.id}
                                                value={localBody}
                                                onChange={(e) => {
                                                    setLocalBody(e.target.value);
                                                    scheduleContentSave(localTitle, e.target.value);
                                                }}
                                                className="text-foreground placeholder:text-muted-foreground bg-card w-full resize-y border-none p-4 text-sm focus:outline-none"
                                                rows={10}
                                                placeholder={t("admin.lms.readingPlaceholder")}
                                            />
                                        </div>
                                    </div>
                                )}
                                {editingContent.__typename === "QuizContent" && (
                                    <div className="flex flex-col gap-4">
                                        <QuizStatCardGroup>
                                            <QuizStatCard label={"Questions"} value={localQuestions.length} />
                                            <QuizStatCard
                                                label={"Pass Threshold"}
                                                content={
                                                    <div className="flex flex-row gap-2">
                                                        <Slider
                                                            value={passThreshold}
                                                            onValueChange={handleThresholdChange}
                                                            max={100}
                                                            step={1}
                                                            className="w-full"
                                                        />
                                                        <Mono size="xs" muted className="text-center">
                                                            {passThreshold[0]}%
                                                        </Mono>
                                                    </div>
                                                }
                                            />
                                            {/* <QuizStatCard label={"Retries"} value={"1"} /> */}
                                        </QuizStatCardGroup>
                                        {localQuestions.map((q, i) => (
                                            <QuizQuestion
                                                key={q.id}
                                                index={i + 1}
                                                questionId={q.id}
                                                questionType={q.questionType}
                                                questionText={q.questionText}
                                                options={q.options ?? undefined}
                                                onUpdate={(input) =>
                                                    onSave(() => updateQuestion({ variables: { id: q.id, input } }))
                                                }
                                                onDelete={() => handleDeleteQuestion(q.id)}
                                            />
                                        ))}
                                        <button
                                            onClick={addQuestion}
                                            className="border-border text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center gap-2 rounded-md border-2 border-dashed p-4 transition-colors"
                                        >
                                            <PlusIcon className="text-secondary h-4 w-4" />
                                            <Text>{t("admin.lms.addQuestion")}</Text>
                                        </button>
                                    </div>
                                )}
                                <div className="bg-card border-border flex flex-col gap-3 rounded-xl border p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Text weight="semibold">{t("common.fields.brands")}</Text>
                                            <Text size="sm" muted>
                                                {t("admin.lms.brandDescription")}
                                            </Text>
                                        </div>
                                        <Mono size="xs" muted>
                                            {selectedBrands.size} applied
                                        </Mono>
                                    </div>
                                    {selectedBrands.size > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {allBrands
                                                .filter((brand) => selectedBrands.has(brand.slug))
                                                .map((brand) => (
                                                    <button
                                                        key={brand.id}
                                                        onClick={() => toggleBrand(brand.slug)}
                                                        className="border-border bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                                                    >
                                                        {brand.name}
                                                        <span className="text-xs">×</span>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                    <DropdownMenu open={brandsOpen} onOpenChange={setBrandsOpen}>
                                        <DropdownMenuTrigger asChild>
                                            <Badge
                                                variant={"dottedOutline"}
                                                className={cn(brandsOpen && "border-secondary border-dashed", "w-fit")}
                                            >
                                                <Plus className="mr-1 h-3.5 w-3.5" /> {t("admin.lms.addBrand")}
                                            </Badge>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="bg-card w-48" align="start">
                                            <DropdownMenuGroup>
                                                {allBrands.map((brand) => (
                                                    <DropdownMenuItem
                                                        key={brand.id}
                                                        onSelect={(e) => {
                                                            e.preventDefault();
                                                            toggleBrand(brand.slug);
                                                        }}
                                                        className="flex items-center justify-between"
                                                    >
                                                        {brand.name}
                                                        {selectedBrands.has(brand.slug) && (
                                                            <Check className="text-secondary h-3.5 w-3.5" />
                                                        )}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuGroup>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {selectedBrands.size === 0 && (
                                        <Text size="sm" muted className="italic">
                                            {t("admin.lms.brandsHelper")}
                                        </Text>
                                    )}
                                </div>
                                {/*
                                 * Tags sit beside brands and look the same on purpose, but they
                                 * answer a different question: a brand decides *who may see* this
                                 * item, a tag describes *what it is about*. Same control, so the
                                 * distinction is the label rather than a new interaction to learn.
                                 */}
                                <div className="bg-card border-border flex flex-col gap-3 rounded-xl border p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Text weight="semibold">Tags</Text>
                                            <Text size="sm" muted>
                                                What this is about. Shared with courses, so a rename applies everywhere.
                                            </Text>
                                        </div>
                                        <Mono size="xs" muted>
                                            {selectedTags.size} applied
                                        </Mono>
                                    </div>
                                    {selectedTags.size > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {allTags
                                                .filter((tag) => selectedTags.has(tag.id))
                                                .map((tag) => (
                                                    <button
                                                        key={tag.id}
                                                        onClick={() => toggleTag(tag.id)}
                                                        aria-label={`Remove ${tag.name}`}
                                                        className="border-border bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                                                    >
                                                        {tag.name}
                                                        <span className="text-xs">×</span>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                    <DropdownMenu open={tagsOpen} onOpenChange={setTagsOpen}>
                                        <DropdownMenuTrigger asChild>
                                            <Badge
                                                variant={"dottedOutline"}
                                                className={cn(tagsOpen && "border-secondary border-dashed", "w-fit")}
                                            >
                                                <Plus className="mr-1 h-3.5 w-3.5" /> Add tag
                                            </Badge>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="bg-card w-48" align="start">
                                            <DropdownMenuGroup>
                                                {allTags.map((tag) => (
                                                    <DropdownMenuItem
                                                        key={tag.id}
                                                        onSelect={(e) => {
                                                            e.preventDefault();
                                                            toggleTag(tag.id);
                                                        }}
                                                        className="flex items-center justify-between"
                                                    >
                                                        {tag.name}
                                                        {selectedTags.has(tag.id) && (
                                                            <Check className="text-secondary h-3.5 w-3.5" />
                                                        )}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuGroup>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    {allTags.length === 0 && (
                                        <Text size="sm" muted className="italic">
                                            No tags exist yet — create them on the Tags page.
                                        </Text>
                                    )}
                                </div>
                                <div className="bg-card border-border flex items-center justify-between gap-3 rounded-xl border p-4">
                                    <div>
                                        <Text weight="semibold">{t("common.fields.ageRestricted")}</Text>
                                        <Text size="sm" muted>
                                            {t("admin.lms.ageRestrictedDescription")}
                                        </Text>
                                    </div>
                                    <Switch
                                        aria-label={t("common.fields.ageRestricted")}
                                        checked={ageRestricted}
                                        onCheckedChange={toggleAgeRestricted}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};
